import { Field, useFieldContext } from "@ark-ui/react/field";
import { useIsomorphicEffect } from "@base/model/hooks/useIsomorphicEffect";
import { cva } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";
import { useRef, useState } from "react";

type InputProps = ComponentProps<typeof Field.Input>;
type CustomProps = {
  size?: "small" | "medium" | "large";
  variant?: "outline" | "underline";
  prefix?: ReactNode;
  suffix?: ReactNode;
};
type Props = Omit<InputProps, keyof CustomProps> & CustomProps;

const inputVariants = cva("transition-colors focus-visible:outline-none text-gray-1000 w-full", {
  variants: {
    variant: {
      outline: "border",
      underline: "border-b",
    },
    size: {
      small: "h-8 placeholder:text-label3 text-label3",
      medium: "h-10 placeholder:text-label2 text-label2",
      large: "h-12 placeholder:text-label1 text-label1",
    },
    state: {
      default: "placeholder:text-gray-500",
      readOnly: "bg-gray-10 placeholder:text-gray-500",
      disabled: "text-gray-300 bg-gray-100 placeholder:text-gray-300 border-gray-200",
      error: "placeholder:text-gray-500",
    },
  },
  compoundVariants: [
    {
      variant: "outline",
      size: "small",
      class: "rounded-4",
    },
    {
      variant: "outline",
      size: "medium",
      class: "rounded-6",
    },
    {
      variant: "outline",
      size: "large",
      class: "rounded-8",
    },
    {
      variant: "outline",
      state: "error",
      class: "border-red-500 bg-red-10",
    },
    {
      variant: "underline",
      state: "error",
      class: "border-b-red-500",
    },
    {
      variant: "outline",
      state: "readOnly",
      class: "border-gray-500",
    },
    {
      variant: "underline",
      state: "readOnly",
      class: "border-b-gray-500",
    },
    {
      variant: "outline",
      state: "disabled",
      class: "border-gray-300",
    },
    {
      variant: "underline",
      state: "disabled",
      class: "border-b-gray-300",
    },
    {
      variant: "outline",
      state: "default",
      class: "border-gray-500 hover:border-gray-600 focus:border-gray-1000",
    },
    {
      variant: "underline",
      state: "default",
      class: "border-b-gray-500 hover:border-b-gray-600 focus:border-b-gray-1000",
    },
  ],
  defaultVariants: {
    variant: "outline",
    size: "medium",
    state: "default",
  },
});

const getIconPositionClass = (size: NonNullable<CustomProps["size"]>, position: "left" | "right") => {
  const positionMap = {
    small: {
      left: "left-2",
      right: "right-2",
    },
    medium: {
      left: "left-3",
      right: "right-3",
    },
    large: {
      left: "left-4",
      right: "right-4",
    },
  };

  return positionMap[size][position];
};

const Input = ({ size = "medium", variant = "outline", prefix: PrefixIcon, suffix: SuffixIcon, ...rest }: Props) => {
  const { disabled, readOnly, invalid } = useFieldContext();
  const prefixRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);

  const [padding, setPadding] = useState({
    left: {
      small: 8,
      medium: 12,
      large: 12,
    }[size],
    right: {
      small: 8,
      medium: 12,
      large: 12,
    }[size],
  });

  useIsomorphicEffect(() => {
    const prefixWidth = prefixRef.current?.offsetWidth || 0;
    const suffixWidth = suffixRef.current?.offsetWidth || 0;
    const basePadding = {
      small: 8,
      medium: 12,
      large: 12,
    }[size];

    const iconOffset = {
      small: 8,
      medium: 12,
      large: 16,
    }[size];

    setPadding({
      left: PrefixIcon ? prefixWidth + basePadding + iconOffset : basePadding,
      right: SuffixIcon ? suffixWidth + basePadding + iconOffset : basePadding,
    });
  }, [PrefixIcon, SuffixIcon, size]);

  const hasIcon = !!PrefixIcon || !!SuffixIcon;

  const state = disabled ? "disabled" : readOnly ? "readOnly" : invalid ? "error" : "default";

  const inputClassName = inputVariants({
    size,
    variant,
    state,
  });

  const inputStyle = {
    paddingLeft: `${padding.left}px`,
    paddingRight: `${padding.right}px`,
  };

  if (hasIcon) {
    return (
      <div className="relative w-full">
        <Field.Input
          disabled={state === "disabled"}
          readOnly={state === "readOnly"}
          className={inputClassName}
          style={inputStyle}
          {...rest}
        />
        {PrefixIcon && (
          <span
            ref={prefixRef}
            className={`absolute top-1/2 -translate-y-1/2 ${getIconPositionClass(size, "left")} inline-flex items-center justify-center`}
          >
            {PrefixIcon}
          </span>
        )}
        {SuffixIcon && (
          <span
            ref={suffixRef}
            className={`absolute top-1/2 -translate-y-1/2 ${getIconPositionClass(size, "right")} inline-flex items-center justify-center`}
          >
            {SuffixIcon}
          </span>
        )}
      </div>
    );
  }

  return (
    <Field.Input
      disabled={state === "disabled"}
      readOnly={state === "readOnly"}
      className={inputClassName}
      style={inputStyle}
      {...rest}
    />
  );
};

export default Input;
